<template>
  <div class="row">
    <!-- Header -->
    <div class="col-12 mb-4">
      <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <div>
          <h2 class="text-white font-weight-bold mb-1">{{ $t('player.title') }}</h2>
          <p class="text-muted small mb-0">{{ $t('player.subtitle') }}</p>
        </div>
        <div class="mt-2 mt-md-0 d-flex align-items-center gap-2 flex-wrap">
          <router-link :to="{ name: 'BillingPay', query: { mode: 'addon' } }" class="btn btn-outline-warning px-3 py-2 font-weight-bold">
            <i class="mdi mdi-account-plus mr-1"></i> {{ $t('player.upgradeQuota') }}
          </router-link>
          <router-link :to="{ name: 'CreatePlayer' }" class="btn btn-primary px-3 py-2 font-weight-bold">
            <i class="mdi mdi-plus mr-1"></i> {{ $t('player.insertNew') }}
          </router-link>
        </div>
      </div>
    </div>

    <!-- Top Seat Quota Usage Telemetry Card -->
    <div class="col-12 mb-4">
      <div class="card card-glass p-3 p-md-4" style="background: linear-gradient(135deg, rgba(20, 24, 39, 0.95) 0%, rgba(13, 17, 28, 0.95) 100%); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px;">
        <div class="row align-items-center">
          <div class="col-lg-7 col-md-12 mb-3 mb-lg-0">
            <div class="d-flex align-items-center gap-2 mb-2">
              <span class="badge badge-warning text-dark font-weight-bold px-2 py-1">
                <i class="mdi mdi-seat-passenger mr-1"></i> {{ $t('player.quotaUsage') }}
              </span>
              <span class="text-muted small">
                {{ companyName }}
              </span>
            </div>
            <h4 class="text-white font-weight-bold mb-1">
              {{ seatsUsed }} / {{ totalSeats }} <span class="text-muted font-weight-normal" style="font-size: 1rem;">{{ $t('quota.seats') }}</span>
              <span class="badge ms-2" :class="quotaBadgeClass" style="font-size: 0.85rem;">
                {{ quotaPercent }}% {{ $t('quota.capacity') }}
              </span>
            </h4>
            <p class="text-muted small mb-2">{{ $t('player.quotaSubtitle') }}</p>
            
            <!-- Progress Bar -->
            <div class="progress" style="height: 10px; background-color: rgba(255, 255, 255, 0.08); border-radius: 6px;">
              <div
                class="progress-bar progress-bar-striped progress-bar-animated"
                :class="quotaBarClass"
                role="progressbar"
                :style="{ width: `${quotaPercent}%` }"
                :aria-valuenow="quotaPercent"
                aria-valuemin="0"
                aria-valuemax="100"
              ></div>
            </div>
          </div>

          <div class="col-lg-5 col-md-12">
            <div class="row g-2 text-center">
              <div class="col-4">
                <div class="p-2 rounded" style="background: rgba(255, 255, 255, 0.03); border: 1px solid rgba(255, 255, 255, 0.06);">
                  <div class="text-muted small text-uppercase" style="font-size: 11px;">{{ $t('player.totalSeats') }}</div>
                  <div class="text-white font-weight-bold h5 mb-0 mt-1">{{ totalSeats }}</div>
                </div>
              </div>
              <div class="col-4">
                <div class="p-2 rounded" style="background: rgba(0, 144, 231, 0.08); border: 1px solid rgba(0, 144, 231, 0.2);">
                  <div class="text-info small text-uppercase" style="font-size: 11px;">{{ $t('player.seatsUsed') }}</div>
                  <div class="text-white font-weight-bold h5 mb-0 mt-1">{{ seatsUsed }}</div>
                </div>
              </div>
              <div class="col-4">
                <div class="p-2 rounded" style="background: rgba(0, 210, 91, 0.08); border: 1px solid rgba(0, 210, 91, 0.2);">
                  <div class="text-success small text-uppercase" style="font-size: 11px;">{{ $t('player.seatsAvailable') }}</div>
                  <div class="text-white font-weight-bold h5 mb-0 mt-1">{{ seatsAvailable }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Table Card -->
    <div class="col-12 grid-margin">
      <div class="card" style="background: #111420; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 12px;">
        <div class="card-body">
          <!-- Search & Filter Bar -->
          <div class="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
            <div class="d-flex align-items-center gap-2 flex-wrap" style="flex: 1; max-width: 720px;">
              <div class="search-box" style="min-width: 260px; flex: 1;">
                <div class="input-group">
                  <span class="input-group-text bg-dark border-secondary text-muted">
                    <i class="mdi mdi-magnify"></i>
                  </span>
                  <input
                    v-model.trim="search"
                    type="text"
                    class="form-control bg-dark border-secondary text-white"
                    :placeholder="$t('common.search') + ($currentLocale === 'id' ? ' player, email, squad, jabatan...' : ' player, email, squad, position...')"
                  />
                  <button v-if="search" class="btn btn-dark border-secondary text-muted" type="button" @click="search = ''">
                    <i class="mdi mdi-close"></i>
                  </button>
                </div>
              </div>

              <!-- Squad Filter -->
              <select
                v-model="squadFilter"
                class="form-select form-control bg-dark border-secondary text-white"
                style="max-width: 200px;"
              >
                <option value="all">{{ $t('common.all') }} {{ $t('player.squad') }}</option>
                <option v-for="sq in squads" :key="sq.id" :value="sq.id">
                  {{ sq.name }}
                </option>
              </select>
            </div>

            <div class="text-muted small">
              {{ $t('common.all') }}: <strong class="text-white">{{ filteredItems.length }}</strong>
            </div>
          </div>

          <!-- Table -->
          <div class="table-responsive">
            <table class="table table-hover text-white align-middle" style="width: 100%; table-layout: fixed;">
              <thead>
                <tr class="text-muted text-uppercase small" style="border-bottom: 1px solid rgba(255,255,255,0.1);">
                  <th style="width: 40px;">No</th>
                  <th style="width: 230px;">{{ $t('common.name') }}</th>
                  <th style="width: 160px;">{{ $t('player.squad') }}</th>
                  <th style="width: 210px;">{{ $currentLocale === 'id' ? 'Gamifikasi' : 'Gamification' }}</th>
                  <th style="width: 110px;">{{ $t('common.role') }}</th>
                  <th style="width: 110px;">{{ $t('superadmin.joinDate') }}</th>
                  <th class="text-end text-right actions-cell" style="width: 170px; white-space: nowrap;">{{ $t('common.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(data, index) in paginatedPlayer"
                  :key="data.id"
                  style="border-bottom: 1px solid rgba(255,255,255,0.05);"
                >
                  <td class="text-muted">{{ index + 1 + (currentPage - 1) * itemsPerPage }}</td>
                  
                  <!-- Employee / Player -->
                  <td>
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle me-3 mr-3 d-flex align-items-center justify-content-center bg-dark text-info font-weight-bold"
                        style="width: 38px; height: 38px; min-width: 38px; border: 1px solid rgba(0, 144, 231, 0.3);"
                      >
                        {{ (data.name || 'P').charAt(0).toUpperCase() }}
                      </div>
                      <div style="min-width: 0; overflow: hidden;">
                        <router-link
                          :to="{ name: 'DetailPlayer', params: { id: data.id } }"
                          class="font-weight-bold text-white text-decoration-none text-truncate d-block"
                        >
                          {{ data.name || '-' }}
                        </router-link>
                        <div class="text-muted small text-truncate" style="font-size: 11px;">
                          {{ data.email || '-' }}
                        </div>
                        <div v-if="data.position" class="mt-1">
                          <span class="badge badge-outline-secondary px-1 py-0 text-truncate d-inline-block" style="font-size: 10px; max-width: 170px;">
                            {{ data.position }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <!-- Squad / Division -->
                  <td>
                    <span v-if="data.division" class="badge badge-outline-info px-2 py-1 text-truncate d-inline-block" style="max-width: 150px;" :title="data.division.name">
                      <i class="mdi mdi-account-group mr-1"></i>
                      {{ data.division.name }}
                    </span>
                    <span v-else class="text-muted small">-</span>
                  </td>

                  <!-- Gamification Telemetry -->
                  <td>
                    <div class="d-flex align-items-center gap-1 flex-wrap">
                      <span class="badge badge-primary px-1 py-1" style="font-size: 10px;">
                        Lv. {{ data.level || 1 }}
                      </span>
                      <span class="badge badge-outline-primary px-1 py-1" style="font-size: 10px;">
                        {{ data.xp || 0 }} XP
                      </span>
                      <span class="badge badge-warning text-dark font-weight-bold px-1 py-1" style="font-size: 10px;">
                        <i class="mdi mdi-gold mr-1"></i>{{ data.gold || 0 }}
                      </span>
                      <span class="badge badge-outline-danger px-1 py-1" style="font-size: 10px;" :title="$currentLocale === 'id' ? 'Rentetan Hari Aktif' : 'Current Streak'">
                        <i class="mdi mdi-fire mr-1"></i>{{ data.current_streak || 0 }}d
                      </span>
                    </div>
                  </td>

                  <!-- Role -->
                  <td>
                    <span v-if="isDualRole(data.role)" class="badge badge-success px-2 py-1" style="font-size: 11px;">
                      <i class="mdi mdi-shield-check mr-1"></i> Maker+Player
                    </span>
                    <span v-else class="badge badge-info px-2 py-1" style="font-size: 11px;">
                      <i class="mdi mdi-account mr-1"></i> Player
                    </span>
                  </td>

                  <!-- Join Date -->
                  <td class="text-muted small" style="white-space: nowrap;">
                    {{ formatDate(data.created_at) }}
                  </td>

                  <!-- Actions Suite -->
                  <td class="text-end text-right actions-cell" style="white-space: nowrap; width: 170px;">
                    <div class="d-inline-flex align-items-center justify-content-end gap-1">
                      <!-- Quick Edit Modal -->
                      <button
                        @click="openEditModal(data)"
                        class="btn btn-outline-info btn-action-icon"
                        :title="$t('player.editPlayer')"
                      >
                        <i class="mdi mdi-pencil"></i>
                      </button>

                      <!-- Reset Password -->
                      <button
                        @click="openResetPasswordModal(data)"
                        class="btn btn-outline-warning btn-action-icon"
                        :title="$t('player.resetPassword')"
                      >
                        <i class="mdi mdi-key-variant"></i>
                      </button>

                      <!-- Maker Role Toggle -->
                      <button
                        v-if="!isDualRole(data.role)"
                        @click="handleAddToMaker(data.id)"
                        class="btn btn-outline-success btn-action-icon"
                        :title="$t('player.addToMaker')"
                      >
                        <i class="mdi mdi-shield-account"></i>
                      </button>
                      <button
                        v-else
                        @click="handleRemoveFromMaker(data.id)"
                        class="btn btn-outline-secondary btn-action-icon"
                        :title="$t('player.confirmDemoteMakerBtn')"
                      >
                        <i class="mdi mdi-shield-off"></i>
                      </button>

                      <!-- Offboard / Delete -->
                      <button
                        @click="confirmDelete(data)"
                        class="btn btn-outline-danger btn-action-icon"
                        :title="$t('player.deletePlayer')"
                      >
                        <i class="mdi mdi-delete-outline"></i>
                      </button>
                    </div>
                  </td>
                </tr>

                <tr v-if="paginatedPlayer.length === 0">
                  <td colspan="7" class="text-center py-5">
                    <EmptyState
                      icon="mdi-account-multiple-outline"
                      :title="$t('player.noPlayersFound')"
                      :description="$t('player.noPlayersDesc')"
                      :actionText="$t('player.insertNew')"
                      actionTo="/player/add"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination -->
          <div v-if="filteredItems.length > itemsPerPage" class="mt-4">
            <PaginationComponents
              :totalItems="filteredItems.length"
              :itemsPerPage="itemsPerPage"
              :currentPage="currentPage"
              @page-changed="onPageChanged"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Edit Player Modal -->
    <div
      v-if="isEditModalOpen"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 1050; backdrop-filter: blur(8px);"
    >
      <div class="card card-glass text-white" style="width: 520px; max-width: 95%; background: #141724; border: 1px solid rgba(255,255,255,0.12); border-radius: 14px;">
        <div class="card-header border-secondary d-flex justify-content-between align-items-center p-3">
          <h5 class="modal-title font-weight-bold text-white mb-0 d-flex align-items-center gap-2">
            <i class="mdi mdi-account-edit text-info"></i>
            <span>{{ $t('player.editModalTitle') }}</span>
          </h5>
          <button type="button" class="close text-white" style="opacity:0.85;font-size:1.5rem;background:none;border:none;" @click="closeEditModal">&times;</button>
        </div>
        <div class="card-body p-4">
          <form @submit.prevent="submitEdit">
            <div class="mb-3">
              <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('common.name') }} <span class="text-danger">*</span></label>
              <input
                type="text"
                v-model="editForm.name"
                class="form-control bg-dark border-secondary text-white"
                required
              />
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('auth.emailLabel') }} <span class="text-danger">*</span></label>
                <input
                  type="email"
                  v-model="editForm.email"
                  class="form-control bg-dark border-secondary text-white"
                  required
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('playerForm.phoneNumber') }}</label>
                <input
                  type="text"
                  v-model="editForm.phone_number"
                  class="form-control bg-dark border-secondary text-white"
                  placeholder="08123456789"
                />
              </div>
            </div>

            <div class="row">
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.position') }}</label>
                <input
                  type="text"
                  v-model="editForm.position"
                  class="form-control bg-dark border-secondary text-white"
                  :placeholder="$currentLocale === 'id' ? 'Contoh: Frontend Engineer' : 'e.g. Frontend Engineer'"
                />
              </div>
              <div class="col-md-6 mb-3">
                <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.squad') }}</label>
                <select
                  v-model="editForm.division_id"
                  class="form-select form-control bg-dark border-secondary text-white"
                >
                  <option :value="null">-- {{ $t('playerForm.selectSquad') }} --</option>
                  <option v-for="sq in squads" :key="sq.id" :value="sq.id">
                    {{ sq.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary px-3" @click="closeEditModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-primary px-4 font-weight-bold" :disabled="editLoading">
                <span v-if="editLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('common.save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Reset Password Modal -->
    <div
      v-if="isResetModalOpen"
      class="custom-modal-backdrop d-flex align-items-center justify-content-center"
      style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.85); z-index: 1050; backdrop-filter: blur(8px);"
    >
      <div class="card card-glass text-white" style="width: 460px; max-width: 95%; background: #141724; border: 1px solid rgba(255,255,255,0.12); border-radius: 14px;">
        <div class="card-header border-secondary d-flex justify-content-between align-items-center p-3">
          <h5 class="modal-title font-weight-bold text-white mb-0 d-flex align-items-center gap-2">
            <i class="mdi mdi-key-variant text-warning"></i>
            <span>{{ $t('player.resetPasswordTitle') }}</span>
          </h5>
          <button type="button" class="close text-white" style="opacity:0.85;font-size:1.5rem;background:none;border:none;" @click="closeResetModal">&times;</button>
        </div>
        <div class="card-body p-4">
          <p class="text-muted small mb-3">
            {{ $t('player.resetPasswordSub') }} <strong class="text-white">{{ activePlayer?.name }}</strong> ({{ activePlayer?.email }})
          </p>

          <form @submit.prevent="submitResetPassword">
            <div class="mb-3">
              <label class="form-label text-muted small text-uppercase font-weight-bold">{{ $t('player.newPassword') }} <span class="text-danger">*</span></label>
              <div class="input-group">
                <input
                  :type="showNewPassword ? 'text' : 'password'"
                  v-model="newPassword"
                  class="form-control bg-dark border-secondary text-white"
                  :placeholder="$currentLocale === 'id' ? 'Minimal 6 karakter' : 'Min. 6 characters'"
                  required
                  minlength="6"
                />
                <button
                  type="button"
                  class="btn btn-dark border-secondary text-muted"
                  @click="showNewPassword = !showNewPassword"
                >
                  <i :class="showNewPassword ? 'mdi mdi-eye-off text-warning' : 'mdi mdi-eye'"></i>
                </button>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-3">
              <button
                type="button"
                class="btn btn-outline-secondary btn-xs py-1"
                @click="generateRandomPassword"
              >
                <i class="mdi mdi-dice-5-outline mr-1"></i> {{ $currentLocale === 'id' ? 'Buat Password Otomatis' : 'Auto Generate Password' }}
              </button>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <button type="button" class="btn btn-secondary px-3" @click="closeResetModal">
                {{ $t('common.cancel') }}
              </button>
              <button type="submit" class="btn btn-warning text-dark font-weight-bold px-4" :disabled="resetLoading || !newPassword">
                <span v-if="resetLoading" class="spinner-border spinner-border-sm me-1"></span>
                {{ $t('player.resetPassword') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  getPlayer,
  addToMaker,
  removeFromMaker,
  deletePlayer,
  updatePlayer,
  getSquad,
} from '@/services/playerService';
import moment from 'moment';
import PaginationComponents from '@/components/PaginationComponents.vue';
import EmptyState from '@/components/EmptyState.vue';
import Swal from 'sweetalert2';

export default {
  name: 'ListPlayer',
  components: {
    PaginationComponents,
    EmptyState,
  },
  data() {
    return {
      search: '',
      squadFilter: 'all',
      player: [],
      squads: [],
      currentPage: 1,
      itemsPerPage: 10,
      loading: false,

      // Edit Modal State
      isEditModalOpen: false,
      editLoading: false,
      editForm: {
        id: null,
        name: '',
        email: '',
        phone_number: '',
        position: '',
        division_id: null,
      },

      // Reset Password Modal State
      isResetModalOpen: false,
      resetLoading: false,
      activePlayer: null,
      newPassword: '',
      showNewPassword: false,
    };
  },
  computed: {
    totalSeats() {
      if (this.player.length > 0 && this.player[0].company) {
        return parseInt(this.player[0].company.number_of_players || 100);
      }
      return 100;
    },
    seatsUsed() {
      return this.player.length;
    },
    seatsAvailable() {
      return Math.max(0, this.totalSeats - this.seatsUsed);
    },
    quotaPercent() {
      if (!this.totalSeats) return 0;
      return Math.min(100, Math.round((this.seatsUsed / this.totalSeats) * 100));
    },
    quotaBadgeClass() {
      if (this.quotaPercent >= 90) return 'badge-danger';
      if (this.quotaPercent >= 75) return 'badge-warning';
      return 'badge-success';
    },
    quotaBarClass() {
      if (this.quotaPercent >= 90) return 'bg-danger';
      if (this.quotaPercent >= 75) return 'bg-warning';
      return 'bg-info';
    },
    companyName() {
      if (this.player.length > 0 && this.player[0].company) {
        return this.player[0].company.name || this.player[0].company.company_name;
      }
      return 'Gamiteam Workspace';
    },
    filteredItems() {
      let result = this.player;

      // Squad filter
      if (this.squadFilter !== 'all') {
        result = result.filter(item => item.division_id === this.squadFilter);
      }

      // Search query
      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter((item) => {
          const name = (item.name || '').toLowerCase();
          const email = (item.email || '').toLowerCase();
          const comp = (item.company && (item.company.company_name || item.company.name) || '').toLowerCase();
          const div = (item.division && item.division.name || '').toLowerCase();
          const pos = (item.position || '').toLowerCase();
          return (
            name.includes(q) ||
            email.includes(q) ||
            comp.includes(q) ||
            div.includes(q) ||
            pos.includes(q)
          );
        });
      }

      return result;
    },
    paginatedPlayer() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredItems.slice(start, end);
    },
  },
  watch: {
    search() {
      this.currentPage = 1;
    },
    squadFilter() {
      this.currentPage = 1;
    },
  },
  methods: {
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY');
    },
    isDualRole(role) {
      if (!role) return false;
      return role.includes('maker') && role.includes('player');
    },
    async fetchData() {
      this.loading = true;
      try {
        const [playerRes, squadRes] = await Promise.all([
          getPlayer(),
          getSquad().catch(() => ({ data: [] })),
        ]);
        this.player = playerRes.data || [];
        this.squads = squadRes.data || [];
      } catch (error) {
        console.error('Error fetching players:', error);
      } finally {
        this.loading = false;
      }
    },
    onPageChanged(page) {
      this.currentPage = page;
    },

    // Promote to Maker
    async handleAddToMaker(id) {
      Swal.fire({
        title: this.$t('player.confirmPromoteTitle'),
        text: this.$t('player.confirmPromoteText'),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0090e7',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('player.confirmPromoteBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await addToMaker(id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Player berhasil dipromosikan menjadi Game Maker.' : 'Player successfully promoted to Game Maker.',
              confirmButtonColor: '#0090e7',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error adding to maker:', error);
            Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal mempromosikan player.' : 'Failed to promote player.', 'error');
          }
        }
      });
    },

    // Demote from Maker
    async handleRemoveFromMaker(id) {
      Swal.fire({
        title: this.$t('player.confirmDemoteMakerTitle'),
        text: this.$t('player.confirmDemoteMakerText'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('player.confirmDemoteMakerBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await removeFromMaker(id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$currentLocale === 'id' ? 'Wewenang Maker dicabut. Karyawan tetap aktif sebagai Player.' : 'Maker privileges removed. Employee remains active as player.',
              confirmButtonColor: '#0090e7',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error removing maker role:', error);
            Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal mencabut peran Maker.' : 'Failed to revoke maker role.', 'error');
          }
        }
      });
    },

    // Delete / Offboard Player
    confirmDelete(data) {
      Swal.fire({
        title: this.$t('player.confirmDeleteTitle'),
        text: `${this.$t('player.confirmDeleteText')} (${data.name})`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#6c757d',
        confirmButtonText: this.$t('player.confirmDeleteBtn'),
        cancelButtonText: this.$t('common.cancel'),
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await deletePlayer(data.id);
            Swal.fire({
              icon: 'success',
              title: this.$t('common.success'),
              text: this.$t('playerForm.deletedSuccess'),
              confirmButtonColor: '#0090e7',
            });
            await this.fetchData();
          } catch (error) {
            console.error('Error deleting player:', error);
            Swal.fire(this.$t('common.error'), this.$currentLocale === 'id' ? 'Gagal menghapus player.' : 'Failed to delete player.', 'error');
          }
        }
      });
    },

    // Open Edit Modal
    openEditModal(data) {
      this.editForm = {
        id: data.id,
        name: data.name || '',
        email: data.email || '',
        phone_number: data.phone_number || '',
        position: data.position || '',
        division_id: data.division_id || null,
      };
      this.isEditModalOpen = true;
    },
    closeEditModal() {
      this.isEditModalOpen = false;
    },
    async submitEdit() {
      this.editLoading = true;
      try {
        await updatePlayer(this.editForm.id, {
          name: this.editForm.name,
          email: this.editForm.email,
          phone_number: this.editForm.phone_number,
          position: this.editForm.position,
          division_id: this.editForm.division_id,
        });
        this.closeEditModal();
        Swal.fire({
          icon: 'success',
          title: this.$t('common.success'),
          text: this.$t('playerForm.updatedSuccess'),
          confirmButtonColor: '#0090e7',
        });
        await this.fetchData();
      } catch (error) {
        console.error('Error updating player:', error);
        const msg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal memperbarui profil karyawan.' : 'Failed to update employee profile.');
        Swal.fire(this.$t('common.error'), msg, 'error');
      } finally {
        this.editLoading = false;
      }
    },

    // Open Reset Password Modal
    openResetPasswordModal(data) {
      this.activePlayer = data;
      this.newPassword = '';
      this.showNewPassword = false;
      this.isResetModalOpen = true;
    },
    closeResetModal() {
      this.isResetModalOpen = false;
      this.activePlayer = null;
      this.newPassword = '';
    },
    generateRandomPassword() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
      let pass = '';
      for (let i = 0; i < 10; i++) {
        pass += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      this.newPassword = pass;
      this.showNewPassword = true;
    },
    async submitResetPassword() {
      if (!this.activePlayer || !this.newPassword) return;
      this.resetLoading = true;
      try {
        await updatePlayer(this.activePlayer.id, {
          password: this.newPassword,
        });
        const savedPass = this.newPassword;
        const isId = this.$currentLocale === 'id';
        this.closeResetModal();
        Swal.fire({
          icon: 'success',
          title: this.$t('player.passwordUpdated'),
          html: `<p>${isId ? 'Password sementara baru:' : 'New temporary password:'}</p><code style="font-size: 1.2rem; color: #f59e0b; background: #1e293b; padding: 6px 14px; border-radius: 6px; display: inline-block;">${savedPass}</code><p class="mt-2 text-muted small">${isId ? 'Harap bagikan kredensial ini kepada karyawan.' : 'Please share this credential with the employee.'}</p>`,
          confirmButtonColor: '#0090e7',
        });
      } catch (error) {
        console.error('Error resetting password:', error);
        const msg = error.response?.data?.message || (this.$currentLocale === 'id' ? 'Gagal mereset password.' : 'Failed to reset password.');
        Swal.fire(this.$t('common.error'), msg, 'error');
      } finally {
        this.resetLoading = false;
      }
    },
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>

<style scoped>
.btn-action-icon {
  width: 28px !important;
  min-width: 28px !important;
  max-width: 28px !important;
  height: 28px !important;
  padding: 0 !important;
  margin: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  border-radius: 6px !important;
}

.actions-cell {
  padding-left: 4px !important;
  padding-right: 8px !important;
  width: 170px !important;
}
</style>

